import { action, computed, makeObservable, observable, reaction } from "mobx";
import { createRef } from "react";
import { inject, injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";
import {
  type AdminDashboardSummary,
  type DashboardStats,
  type IWarehouseApi,
  WAREHOUSE_API_TOKEN,
} from "@/api/WarehouseApi";
import { NAVIGATION_STORE_TOKEN, Screens } from "@/stores/navigation";
import type { INavigationStore } from "@/stores/navigation";
import { STORE_TOKEN, type IStore } from "@/stores/store";
import {
  type RealtimeConnectionStatus,
  REALTIME_STORE_TOKEN,
  type RealtimeFeedItem,
} from "@/stores/RealtimeStore";
import type { IRealtimeStore } from "@/stores/RealtimeStore";
import {
  SETTINGS_STORE_TOKEN,
  type ISettingsStore,
} from "@/stores/SettingsStore";
import { UI_STORE_TOKEN } from "@/stores/UIStore";
import type { IUIStore } from "@/stores/UIStore";
import { getStoredUser } from "@/utils/authStorage";
import {
  DASHBOARD_EVENTS_LIMIT,
  buildDashboardEvents,
} from "./viewmodel/events";
import { openStatsListByKey } from "./viewmodel/stats-navigation";
import {
  loadAdminSummaryData,
  loadLastOperationsData,
  loadWarehouseStatsData,
  logoutDashboard,
  openDashboardEvent,
  reconnectDashboardRealtime,
  refreshDashboardData,
  type MainDashboardOperationState,
} from "./viewmodel/dashboard-operations";
import type { WarehouseStatsCardKey } from "./components/WarehouseStats/WarehouseStats";
import type { LastOperationsStatusTone } from "./components/LastOperations/LastOperations";

type WarehouseStats = DashboardStats;
type WarehouseAdminSummary = AdminDashboardSummary;

const EMPTY_STATS: WarehouseStats = {
  productsTotal: 0,
  boxesTotal: 0,
  productsInBoxes: 0,
  productsWithoutBox: 0,
  expiringSoon: 0,
  expired: 0,
};

const EMPTY_ADMIN_SUMMARY: WarehouseAdminSummary = {
  totalUsers: 0,
  activeUsers: 0,
  pendingUsers: 0,
  deletedProducts: 0,
  deletedBoxes: 0,
};

@injectable()
export class MainViewModel extends ViewModel {
  @observable.shallow
  journalEvents: RealtimeFeedItem[] = [];

  @observable
  isJournalLoading = false;

  @observable
  journalError: string | null = null;

  @observable.ref
  stats: WarehouseStats = EMPTY_STATS;

  @observable
  isStatsLoading = false;

  @observable
  statsError: string | null = null;

  @observable.ref
  adminSummary: WarehouseAdminSummary = EMPTY_ADMIN_SUMMARY;

  @observable
  adminSummaryError: string | null = null;

  @observable
  isAdminSummaryLoading = false;

  @observable
  isLoggingOut = false;

  @observable
  isMenuOpen = false;

  readonly menuRef = createRef<HTMLDivElement>();

  private reloadLastOperationsTimer: ReturnType<typeof setTimeout> | null =
    null;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(UI_STORE_TOKEN) private readonly uiStore: IUIStore,
    @inject(SETTINGS_STORE_TOKEN)
    private readonly settingsStore: ISettingsStore,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
    @inject(REALTIME_STORE_TOKEN)
    private readonly realtimeStore: IRealtimeStore,
  ) {
    super();

    makeObservable(this);
  }

  @computed
  get isSearch(): boolean {
    return this.uiStore.isSearch;
  }

  @computed
  get events(): RealtimeFeedItem[] {
    return buildDashboardEvents({
      realtimeEvents: this.realtimeStore.events,
      journalEvents: this.journalEvents,
      showSystemEvents: this.settingsStore.showSystemEventsInDashboard,
      limit: DASHBOARD_EVENTS_LIMIT,
    });
  }

  @computed
  get realtimeStatus(): string {
    return this.realtimeStore.statusLabel;
  }

  @computed
  get realtimeStatusTone(): LastOperationsStatusTone {
    const status: RealtimeConnectionStatus = this.realtimeStore.status;
    if (status === "connected") {
      return "success";
    }
    if (
      status === "connecting" ||
      status === "reconnecting" ||
      status === "offline"
    ) {
      return "warning";
    }
    if (status === "error" || status === "disabled") {
      return "danger";
    }
    return "muted";
  }

  @computed
  get showRealtimeReconnectAction(): boolean {
    return Boolean(this.realtimeStore.lastError);
  }

  @computed
  get canReconnectRealtime(): boolean {
    return this.realtimeStore.canReconnect;
  }

  @computed
  get reconnectCooldownSeconds(): number {
    return this.realtimeStore.reconnectCooldownSeconds;
  }

  @computed
  get operationsError(): string | null {
    return this.journalError || this.realtimeStore.lastError;
  }

  @computed
  get isOperationsLoading(): boolean {
    return this.isJournalLoading && this.events.length === 0;
  }

  @computed
  get warehouseStats(): WarehouseStats {
    return this.stats;
  }

  @computed
  get warehouseAdminSummary(): WarehouseAdminSummary {
    return this.adminSummary;
  }

  @computed
  get isAdmin(): boolean {
    return getStoredUser()?.role === "admin";
  }

  @computed
  get pendingUsersCount(): number {
    return this.adminSummary.pendingUsers;
  }

  @computed
  get warehouseStatsError(): string | null {
    return this.statsError || this.adminSummaryError;
  }

  @computed
  get isWarehouseStatsLoading(): boolean {
    return this.isStatsLoading || (this.isAdmin && this.isAdminSummaryLoading);
  }

  @computed
  get isStatsEmpty(): boolean {
    return this.stats.productsTotal === 0 && this.stats.boxesTotal === 0;
  }

  protected onViewMounted(): void {
    if (typeof document !== "undefined") {
      document.addEventListener("pointerdown", this.closeMenuOnOutsidePointer);
      document.addEventListener("keydown", this.closeMenuOnEscape);
      this.addDisposer(() => {
        document.removeEventListener(
          "pointerdown",
          this.closeMenuOnOutsidePointer,
        );
        document.removeEventListener("keydown", this.closeMenuOnEscape);
      });
    }

    this.addDisposer(
      reaction(
        () => this.realtimeStore.events[0]?.id || "",
        () => {
          this.scheduleLastOperationsReloadByRealtime();
        },
      ),
    );
    this.addDisposer(() => {
      this.clearLastOperationsReloadTimer();
    });

    void this.refreshDashboard();
  }

  @action
  toQR = (): void => {
    this.navigationStore.setScreen(Screens.QR);
  };

  @action
  toQRManagement = (): void => {
    this.navigationStore.setScreen(Screens.QRManagement);
  };

  @action
  toJournal = (): void => {
    this.navigationStore.setScreen(Screens.Journal);
  };

  @action
  toAddPage = (): void => {
    this.navigationStore.setScreen(Screens.AddProduct);
  };

  @action
  toAddBox = (): void => {
    this.navigationStore.setScreen(Screens.AddBox);
  };

  @action
  toUsersManagement = (): void => {
    this.navigationStore.setScreen(Screens.UsersManagement);
  };

  @action
  toDeletedEntities = (): void => {
    this.navigationStore.setScreen(Screens.DeletedEntities);
  };

  @action
  toAccount = (): void => {
    this.navigationStore.setScreen(Screens.Account);
  };

  @action
  toSettings = (): void => {
    this.navigationStore.setScreen(Screens.Settings);
  };

  @action.bound
  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  @action.bound
  closeMenu(): void {
    this.isMenuOpen = false;
  }

  @action.bound
  onMenuAction(actionToRun: () => void): void {
    this.closeMenu();
    actionToRun();
  }

  @action.bound
  logoutFromMenu(): void {
    this.closeMenu();
    void this.logout();
  }

  @action
  openStatsList = (key: WarehouseStatsCardKey): void => {
    openStatsListByKey(this.navigationStore, key);
  };

  @action
  logout = async (): Promise<void> => {
    await logoutDashboard({
      state: this.dashboardState,
      api: this.api,
      realtimeStore: this.realtimeStore,
      navigationStore: this.navigationStore,
    });
  };

  @action
  reconnectRealtime = (): void => {
    reconnectDashboardRealtime({
      realtimeStore: this.realtimeStore,
      loadLastOperations: () => this.loadLastOperations(),
    });
  };

  @action.bound
  async openEvent(event: RealtimeFeedItem): Promise<void> {
    await openDashboardEvent({
      state: this.dashboardState,
      event,
      api: this.api,
      store: this.store,
      navigationStore: this.navigationStore,
    });
  }

  @action
  refreshDashboard = async (): Promise<void> => {
    await refreshDashboardData({
      loadLastOperations: () => this.loadLastOperations(),
      loadStats: () => this.loadStats(),
      loadAdminSummary: () => this.loadAdminSummary(),
    });
  };

  @action.bound
  async loadLastOperations(limit = DASHBOARD_EVENTS_LIMIT): Promise<void> {
    await loadLastOperationsData({
      state: this.dashboardState,
      api: this.api,
      limit,
    });
  }

  @action.bound
  async loadStats(expiringDays = 7): Promise<void> {
    await loadWarehouseStatsData({
      state: this.dashboardState,
      api: this.api,
      expiringDays,
    });
  }

  @action.bound
  async loadAdminSummary(): Promise<void> {
    await loadAdminSummaryData({
      state: this.dashboardState,
      api: this.api,
      isAdmin: this.isAdmin,
      emptyAdminSummary: EMPTY_ADMIN_SUMMARY,
    });
  }

  private readonly closeMenuOnOutsidePointer = (event: PointerEvent): void => {
    if (!this.isMenuOpen) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    const menuRoot = this.menuRef.current;
    if (!menuRoot) {
      return;
    }

    if (!menuRoot.contains(target)) {
      this.closeMenu();
    }
  };

  private readonly closeMenuOnEscape = (event: KeyboardEvent): void => {
    if (!this.isMenuOpen) {
      return;
    }

    if (event.key === "Escape") {
      this.closeMenu();
    }
  };

  private scheduleLastOperationsReloadByRealtime(): void {
    const latestEvent = this.realtimeStore.events[0];
    if (!latestEvent) {
      return;
    }

    if (!latestEvent.type.startsWith("entity.")) {
      return;
    }

    this.clearLastOperationsReloadTimer();
    this.reloadLastOperationsTimer = setTimeout(() => {
      this.reloadLastOperationsTimer = null;
      void this.loadLastOperations();
    }, 250);
  }

  private clearLastOperationsReloadTimer(): void {
    if (!this.reloadLastOperationsTimer) {
      return;
    }

    clearTimeout(this.reloadLastOperationsTimer);
    this.reloadLastOperationsTimer = null;
  }

  private get dashboardState(): MainDashboardOperationState {
    return this;
  }
}
